import { useEffect, useRef, useState } from 'react'
import { Link, type LinkProps } from 'react-router'
import { onLenisScroll } from '#app/components/smooth-scroller.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { MarketingButton } from './marketing-button.tsx'

const productoItems = [
	{
		name: 'Auri',
		description: 'Asistente legal con acceso a toda la jurisprudencia y normativa costarricense.',
		to: '/producto/auri',
	},
	{
		name: 'Análisis de expedientes',
		description: 'Suba un expediente completo y obtenga un reporte estratégico detallado.',
		to: '/producto/analisis-de-expedientes',
	},
	{
		name: 'Redacción inteligente',
		description: 'Redacte cualquier documento legal basado en los códigos vigentes, sin plantillas.',
		to: '/producto/redaccion-inteligente',
	},
	{
		name: 'Agentes',
		description: 'Automatice tareas jurídicas repetitivas con flujos de trabajo personalizados.',
		to: '/producto/agentes',
	},
	{
		name: 'Tablas IA',
		description: 'Extraiga datos clave de muchos documentos a la vez y compárelos en una tabla.',
		to: '/producto/tablas-ia',
	},
	{
		name: 'Playbooks',
		description: 'Revise documentos en lote según criterios que usted define.',
		to: '/producto/revisiones-automaticas',
	},
	{
		name: 'Notificaciones de la Gaceta',
		description: 'Reciba alertas cuando La Gaceta o el Boletín Judicial publique algo relevante.',
		to: '/producto/notificaciones-de-la-gaceta',
	},
]

// Soluciones por área de práctica. Cada enlace lleva a una página de "cómo le
// ayuda la plataforma" para esa rama del derecho costarricense.
const solucionesPorArea = [
	{ name: 'Derecho Laboral', to: '/soluciones/derecho-laboral' },
	{ name: 'Cobro Judicial', to: '/soluciones/cobro-judicial' },
	{ name: 'Derecho de Familia', to: '/soluciones/derecho-de-familia' },
	{ name: 'Derecho Penal', to: '/soluciones/derecho-penal' },
	{ name: 'Civil y Contratos', to: '/soluciones/civil-y-contratos' },
	{ name: 'Corporativo y Mercantil', to: '/soluciones/corporativo-y-mercantil' },
	{ name: 'Notarial y Registral', to: '/soluciones/notarial-y-registral' },
	{ name: 'Contencioso-Administrativo', to: '/soluciones/contencioso-administrativo' },
	{ name: 'Constitucional y Amparos', to: '/soluciones/constitucional-y-amparos' },
]

// Soluciones por tipo de equipo. Bufetes reutiliza una página existente; el
// resto apunta a su URL canónica.
const solucionesPorTipo = [
	{ name: 'Bufetes y despachos', to: '/despachos' },
	{ name: 'Abogados independientes', to: '/soluciones/abogados-independientes' },
	{ name: 'Departamentos legales', to: '/soluciones/departamentos-legales' },
	{ name: 'Bancos y financieras', to: '/soluciones/bancos' },
	{ name: 'Notarías', to: '/soluciones/notarias' },
]

export function Navbar() {
	const user = useOptionalUser()
	const [scrolled, setScrolled] = useState(false)

	useEffect(() => {
		function onScroll(event?: Event) {
			// The marketing home scrolls the window, but article pages scroll an
			// inner `overflow-y-auto` container. Read whichever one fired.
			const target = event?.target
			const scrollTop = target instanceof HTMLElement ? target.scrollTop : window.scrollY
			setScrolled(scrollTop > 700)
		}
		onScroll()
		// Capture phase so scroll events from inner scroll containers reach us
		// (scroll events don't bubble).
		window.addEventListener('scroll', onScroll, { passive: true, capture: true })
		return () => window.removeEventListener('scroll', onScroll, { capture: true })
	}, [])

	// On pages with `<SmoothScroller />`, Lenis drives the window scroll and
	// native scroll events miss programmatic jumps (e.g. clicking "Planes" and
	// ScrollRestoration scrolling to the hash). Subscribe to Lenis directly so
	// the background updates whenever we end up below the hero.
	useEffect(() => {
		return onLenisScroll(scroll => setScrolled(scroll > 700))
	}, [])

	return (
		<header
			className={cn(
				'fixed left-0 right-0 top-0 z-50 transition-all duration-300',
				scrolled
					? 'border-b border-neutral-200/50 bg-white/80 backdrop-blur-md supports-[backdrop-filter]:bg-white/50'
					: 'border-b border-transparent bg-transparent',
			)}
		>
			<div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 lg:px-8">
				{/* Left: Logo */}
				<Link to="/#" className="flex items-center gap-2.5">
					<div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-900 text-white shadow-sm ring-1 ring-neutral-900/10">
						<img src="/img/logo.png" alt="Ius Pro Logo" className="h-5 w-auto" />
					</div>
					<span className="font-instrument-serif text-sm font-bold tracking-tight text-neutral-900">IUS PRO</span>
				</Link>

				{/* Right: Navigation & Actions */}
				<div className="flex items-center gap-6">
					<nav className="hidden items-center gap-6 md:flex">
						<ProductoDropdown />
						<SolucionesDropdown />
						<TopLink to="/producto/seguridad">Seguridad</TopLink>
						<TopLink to="#planes">Planes</TopLink>
						<TopLink to="#faq">FAQ</TopLink>
						{!user && (
							<Link
								to="/login"
								className="hidden font-sans text-xs font-medium text-neutral-500 transition-colors hover:text-neutral-900 sm:inline-flex"
							>
								Iniciar sesión
							</Link>
						)}
					</nav>
					<MarketingButton size="sm" className="h-8 rounded-none px-5 text-xs tracking-wide" asChild>
						<Link to={user ? '/ai' : '/signup'}>{user ? 'Ir a la App' : 'Crear cuenta'}</Link>
					</MarketingButton>
				</div>
			</div>
		</header>
	)
}

function ProductoDropdown() {
	const [open, setOpen] = useState(false)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	function handleEnter() {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
		setOpen(true)
	}

	function handleLeave() {
		timeoutRef.current = setTimeout(() => setOpen(false), 150)
	}

	return (
		<div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
			<button
				className={cn(
					'flex items-center gap-1 font-sans text-xs font-medium transition-colors',
					open ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900',
				)}
			>
				Producto
				<svg
					className={cn('h-3 w-3 transition-transform duration-200', open && 'rotate-180')}
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
				</svg>
			</button>

			{/* Dropdown panel */}
			<div
				className={cn(
					'absolute -left-4 top-full pt-3 transition-all duration-200',
					open ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0',
				)}
			>
				<div className="w-[420px] rounded-xl border border-neutral-200/60 bg-white/95 p-2 shadow-xl shadow-neutral-900/[0.08] backdrop-blur-xl">
					{productoItems.map(item => (
						<Link
							key={item.name}
							to={item.to}
							className="group flex flex-col gap-0.5 rounded-lg px-4 py-3 transition-all duration-150 hover:bg-neutral-50"
							onClick={() => setOpen(false)}
						>
							<span className="text-[13px] font-medium tracking-tight text-neutral-900 transition-colors group-hover:text-[#c9a962]">
								{item.name}
							</span>
							<span className="text-[12px] leading-relaxed text-neutral-400 transition-colors group-hover:text-neutral-500">
								{item.description}
							</span>
						</Link>
					))}
				</div>
			</div>
		</div>
	)
}

function SolucionesDropdown() {
	const [open, setOpen] = useState(false)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	function handleEnter() {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
		setOpen(true)
	}

	function handleLeave() {
		timeoutRef.current = setTimeout(() => setOpen(false), 150)
	}

	return (
		<div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
			<button
				className={cn(
					'flex items-center gap-1 font-sans text-xs font-medium transition-colors',
					open ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-900',
				)}
			>
				Soluciones
				<svg
					className={cn('h-3 w-3 transition-transform duration-200', open && 'rotate-180')}
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2}
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
				</svg>
			</button>

			{/* Mega-menu panel: dos columnas (por área / por tipo de equipo) */}
			<div
				className={cn(
					'absolute -left-4 top-full pt-3 transition-all duration-200',
					open ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0',
				)}
			>
				<div className="grid w-[560px] grid-cols-[1.2fr_1fr] gap-1 rounded-xl border border-neutral-200/60 bg-white/95 p-2 shadow-xl shadow-neutral-900/[0.08] backdrop-blur-xl">
					<SolucionesColumn title="Por área de práctica" items={solucionesPorArea} onSelect={() => setOpen(false)} />
					<SolucionesColumn
						title="Por tipo de equipo"
						items={solucionesPorTipo}
						onSelect={() => setOpen(false)}
						className="border-l border-neutral-200/60 pl-2"
					/>
				</div>
			</div>
		</div>
	)
}

function SolucionesColumn({
	title,
	items,
	onSelect,
	className,
}: {
	title: string
	items: { name: string; to: string }[]
	onSelect: () => void
	className?: string
}) {
	return (
		<div className={className}>
			<p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">{title}</p>
			{items.map(item => (
				<Link
					key={item.name}
					to={item.to}
					className="block rounded-lg px-3 py-1.5 text-[13px] font-medium tracking-tight text-neutral-600 transition-all duration-150 hover:bg-neutral-50 hover:text-[#c9a962]"
					onClick={onSelect}
				>
					{item.name}
				</Link>
			))}
		</div>
	)
}

function TopLink(props: LinkProps) {
	return (
		<Link
			{...props}
			className={cn(
				'font-sans text-xs font-medium text-neutral-500 transition-colors hover:text-neutral-900',
				props.className,
			)}
		>
			{props.children}
		</Link>
	)
}
